/**
 * Format a birthdate value as MM/DD/YYYY (or MM/DD/YY) using UTC methods.
 *
 * Accepts strings (ISO, US-style), Date objects, null, or undefined.
 * Returns an empty string for falsy / unparseable values.
 *
 * Uses UTC getters so that a date like "2004-02-03T00:00:00.000Z" always
 * displays as 02/03/2004 regardless of the browser's local timezone.
 */
export type BirthdateYearStyle = 'numeric' | '2-digit';

export function formatBirthdate(
    value: string | Date | null | undefined,
    yearStyle: BirthdateYearStyle = 'numeric'
): string {
    if (value == null) return '';

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return '';

        // US-style M/D/YYYY (month/day may be one or two digits) — zero-pad
        // and return. Requiring exactly two digits here used to let a
        // single-digit date like "3/3/1978" fall through to the local-time
        // `new Date(trimmed)` fallback below, which can render the wrong
        // calendar day depending on the browser's timezone.
        const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (usMatch) {
            const [, m, d, y] = usMatch;
            return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${styleYear(y, yearStyle)}`;
        }

        // YYYY-MM-DD (with optional time suffix) — parse manually to avoid timezone shift
        const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const [, y, m, d] = isoMatch;
            return `${m}/${d}/${styleYear(y, yearStyle)}`;
        }

        // Fallback: try Date constructor then format via UTC
        const parsed = new Date(trimmed);
        if (isNaN(parsed.getTime())) return trimmed;
        return formatUTC(parsed, yearStyle);
    }

    if (value instanceof Date) {
        if (isNaN(value.getTime())) return '';
        return formatUTC(value, yearStyle);
    }

    return '';
}

// The `'numeric'` path must return the year exactly as given (no inserted
// leading zeros), so padding only happens here, on the `'2-digit'` branch,
// where it's needed to make the trailing-two-digit slice correct for a year
// with fewer than 4 digits.
function styleYear(year: string, yearStyle: BirthdateYearStyle): string {
    return yearStyle === '2-digit' ? year.padStart(4, '0').slice(-2) : year;
}

function formatUTC(date: Date, yearStyle: BirthdateYearStyle): string {
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    const y = String(date.getUTCFullYear());
    return `${m}/${d}/${styleYear(y, yearStyle)}`;
}
